import useTiltifyConfig from '@hooks/useTiltifyConfig';
import { buildCustomizedURL } from '@util/common';
import { CHARITY, isTestMode } from '@util/const';
import { useMetaTags } from 'react-metatags-hook';
import { useEffect, useMemo, useState } from 'preact/hooks';
import useGCXTotals from '@hooks/useGCXTotals.js';
import GCXTotalsTable from './gcx-totals-table.jsx';
import LoadingSpinner from '@components/shared/loading-spinner.jsx';
import useTiltifyCampaign from '@hooks/useTiltifyCampaign.js';
import GCXCustomizePersonalLeaderboard from './gcx-customize-personal-leaderboard.jsx';
import GCXCustomizePolls from './gcx-customize-polls.jsx';
import GCXCustomizeAlerts from './gcx-customize-alerts.jsx';
import { Refresh } from 'flowbite-react-icons/outline';
import { DateTime } from 'luxon';
import SelfService from '@components/shared/self-service.jsx';

const fullYear = new Date().getUTCFullYear();
const pageName = `GCX ${fullYear} Tools`;
const pageSubtitle = 'Self-Service';
const pageTitle = `${pageName} ${pageSubtitle} | ${isTestMode ? '' : 'St. Jude '}Play Live Tools`;

export default function GCXSelfServicePage() {
  useMetaTags(
    {
      title: pageTitle,
      description: `Self-service page for GCX ${fullYear}`,
      openGraph: {
        type: 'article',
        description: `Self-service page for GCX ${fullYear}`,
      },
    },
    []
  );

  const tiltifyConfig = useTiltifyConfig();
  const [currentDay, setCurrentDay] = useState(null);
  const [currentTimeBlock, setCurrentTimeBlock] = useState(null);
  const [currentPollID, setCurrentPollID] = useState(null);
  const [alertsMuted, setAlertsMuted] = useState(
    localStorage.getItem('gcx-alerts-muted') === 'true'
  );

  const { campaign, campaignLoading } = useTiltifyCampaign({
    userSlug: 'gcx',
    campaignSlug: `gcx-${fullYear}-marathon`,
  });

  const {
    items,
    timeBlockLabels,
    days,
    isLoadingTotals,
    refreshData,
    lastUpdated,
    currentSchedule,
  } = useGCXTotals();

  const { start, end } = useMemo(() => {
    if (currentDay && currentTimeBlock && items.length > 0) {
      const timeBlock = items.find(
        (item) =>
          item.day === currentDay && item.timeBlockLabel === currentTimeBlock
      );

      if (timeBlock !== undefined) {
        return {
          start: timeBlock.startTimestamp,
          end: timeBlock.endTimestamp,
        };
      }
    }

    return {
      start: '',
      end: '',
    };
  }, [currentDay, currentTimeBlock, items]);

  useEffect(() => {
    if (days.length > 0 && items.length > 0) {
      setCurrentDay((curr) => {
        if (curr === null) return days[0];
        return curr;
      });
    }
  }, [days, items]);

  useEffect(() => {
    if (timeBlockLabels.length > 0 && items.length > 0) {
      setCurrentTimeBlock((curr) => {
        if (curr === null) return timeBlockLabels[0];
        return curr;
      });
    }
  }, [timeBlockLabels, items]);

  useEffect(() => {
    if (alertsMuted) {
      localStorage.setItem('gcx-alerts-muted', JSON.stringify(alertsMuted));
    } else {
      localStorage.removeItem('gcx-alerts-muted');
    }
  }, [alertsMuted]);

  const getOverlayURL = (overlayBase, config, suffix = '') => {
    return buildCustomizedURL({
      config: {
        ...tiltifyConfig,
        charityType: CHARITY.TILTIFY,
        tiltifyUserSlug: 'gcx',
        tiltifyCampaignSlug: `gcx-${fullYear}-marathon`,
        ...config,
      },

      urlBase: overlayBase,
      urlSuffix: suffix,
    });
  };

  const gcxOverlays = useMemo(
    () => [
      {
        name: 'Full Overlay',
        id: 'full',
        width: '1150px',
        height: '130px',
        url: getOverlayURL('gcx-progress-bar', {
          type: 'large',
        }),
      },
      {
        name: 'Small Overlay',
        id: 'small',
        width: '650px',
        height: '130px',
        url: getOverlayURL('gcx-progress-bar', {
          type: 'small',
        }),
      },
      {
        name: 'Polls',
        id: 'polls',
        url: getOverlayURL(
          'polls',
          {
            pollsBackgroundColor: 'multi',
            pollsType: 'multiple',
            pollsShowDollars: 'yes',
            pollsShowPercentage: 'yes',
          },
          currentPollID ?? ''
        ),
        disableCopy: currentPollID === null,
        customize: (
          <GCXCustomizePolls
            pollID={currentPollID}
            onPollSelected={setCurrentPollID}
          />
        ),
      },
      {
        name: 'Personal Leaderboard',
        id: 'personal',
        url: decodeURIComponent(
          getOverlayURL('gcx-leaderboard', {
            startTime: start,
            endTime: end,
          })
        ),
        customize: (
          <GCXCustomizePersonalLeaderboard
            days={days}
            items={items}
            isLoadingTotals={isLoadingTotals}
            currentTimeBlock={currentTimeBlock}
            currentDay={currentDay}
            timeBlockLabels={timeBlockLabels}
            setCurrentDay={setCurrentDay}
            setCurrentTimeBlock={setCurrentTimeBlock}
          />
        ),
      },
      {
        name: 'Top 50 Leaderboard',
        id: 'top50',
        width: '500px',
        height: '400px',
        url: getOverlayURL('gcx-leaderboard', {
          timeType: 'all',
          leaderDonors: '50',
        }),
      },
      {
        name: 'Alerts',
        id: 'alerts',
        width: '800px',
        height: '600px',
        url: getOverlayURL(
          'gcx-alerts',
          alertsMuted
            ? {
                sound: 'no',
                minDonation: '0',
              }
            : {
                sound: 'yes',
                volume: '50',
                minDonation: '0',
              }
        ),
        demoModeURL: getOverlayURL(
          'gcx-alerts',
          alertsMuted
            ? {
                sound: 'no',
                minDonation: '0',
                demoMode: 'yes',
              }
            : {
                sound: 'yes',
                volume: '50',
                minDonation: '0',
                demoMode: 'yes',
              }
        ),
        customize: (
          <GCXCustomizeAlerts
            alertsMuted={alertsMuted}
            setAlertsMuted={setAlertsMuted}
          />
        ),
      },
      {
        name: 'Donation Train',
        id: 'donationTrain',
        width: '800px',
        height: '600px',
        url: getOverlayURL('donation-train', {
          bgColor: 'dark-purple',
          donationTrainTheme: 'regular',
          donationTrainPosition: 'top',
          donationTrainMaxVisible: '3',
          showActivationMessage: 'yes',
        }),
        demoModeURL: getOverlayURL('donation-train', {
          bgColor: 'dark-purple',
          donationTrainTheme: 'regular',
          donationTrainPosition: 'top',
          donationTrainMaxVisible: '3',
          showActivationMessage: 'yes',
          demoMode: 'yes',
        }),
      },
    ],
    [
      isLoadingTotals,
      tiltifyConfig,
      start,
      end,
      currentDay,
      currentTimeBlock,
      currentPollID,
      alertsMuted,
    ]
  );

  return (
    <SelfService
      pageName={pageName}
      pageSubtitle={pageSubtitle}
      pageTitle={pageTitle}
      overlays={gcxOverlays}
    >
      <div
        className="ml-0.5 mr-0.5 mt-4 mb-2 select-none flex items-center"
        id="totals"
      >
        <div className="flex flex-col flex-grow">
          <h2 className="text-xl">Totals</h2>

          <span className="text-xs text-gray-500 ml-0.5">
            Last updated:{' '}
            {lastUpdated === null
              ? 'n/a'
              : lastUpdated.toLocaleString(DateTime.DATETIME_FULL)}
          </span>
        </div>
        <div className="tooltip tooltip-left" data-tip="Refresh Totals">
          <button
            className="btn btn-circle btn-sm"
            disabled={isLoadingTotals}
            onClick={() => refreshData()}
          >
            <Refresh />
          </button>
        </div>
      </div>
      <div className="w-full overflow-x-auto">
        <div className="card border-base-300 border shadow mb-2 bg-base-100 w-full">
          <div className="card-body p-0 relative">
            <div className="overflow-x-auto relative min-h-[330px]">
              <LoadingSpinner show={isLoadingTotals || campaignLoading} />

              {!isLoadingTotals && !campaignLoading && currentSchedule && (
                <GCXTotalsTable
                  currentStreamerName={currentSchedule?.streamerName}
                  currentAmount={parseFloat(
                    campaign?.amount_raised?.value ?? '0.00'
                  )}
                  days={days}
                  timeBlockLabels={timeBlockLabels}
                  items={items}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </SelfService>
  );
}
